import { normalizeHashAlgorithm } from "@otplib/core";

import { InvalidURIError, InvalidParameterError, MissingParameterError } from "./types.js";

import type { OTPAuthURI, OTPAuthParams } from "./types.js";
import type { HashAlgorithm, Digits } from "@otplib/core";

// Security limits to prevent DoS attacks
const MAX_URI_LENGTH = 2048; // Standard URL length limit
const MAX_LABEL_LENGTH = 512;
const MAX_PARAM_VALUE_LENGTH = 1024;

type MutableOTPAuthParams = {
  secret?: string;
  issuer?: string;
  algorithm?: HashAlgorithm;
  digits?: Digits;
  counter?: number;
  period?: number;
};

/**
 * Format error message for caught errors
 * @internal
 */
export function formatErrorMessage(error: unknown, context: string): string {
  const errorStr = error instanceof Error ? error.message : String(error);
  return `Invalid URI encoding in ${context}: ${errorStr}`;
}

/**
 * Safely decode a URI component with length validation and error handling
 * @throws {InvalidURIError} If decoding fails or length exceeds limit
 */
function safeDecodeURIComponent(str: string, maxLength: number, context: string): string {
  if (str.length > maxLength * 3) {
    throw new InvalidURIError(`${context} exceeds maximum length`);
  }

  try {
    const decoded = decodeURIComponent(str);
    if (decoded.length > maxLength) {
      throw new InvalidURIError(`${context} exceeds maximum length of ${maxLength} characters`);
    }
    return decoded;
  } catch (error) {
    if (error instanceof InvalidURIError) {
      throw error;
    }
    throw new InvalidURIError(formatErrorMessage(error, context));
  }
}

/**
 * Parse an otpauth:// URI into its components
 *
 * @param uri - The otpauth:// URI to parse
 * @returns Parsed URI components
 * @throws {InvalidURIError} If URI is invalid
 * @throws {MissingParameterError} If required parameters are missing
 * @throws {InvalidParameterError} If parameter values are invalid
 *
 * @example
 * ```ts
 * import { parse } from '@otplib/uri';
 *
 * const uri = 'otpauth://totp/ACME:john@example.com?secret=JBSWY3DPEHPK3PXP&issuer=ACME';
 * const parsed = parse(uri);
 * // {
 * //   type: 'totp',
 * //   label: 'ACME:john@example.com',
 * //   params: { secret: 'JBSWY3DPEHPK3PXP', issuer: 'ACME' }
 * // }
 * ```
 */
export function parse(uri: string): OTPAuthURI {
  if (uri.length > MAX_URI_LENGTH) {
    throw new InvalidURIError(`URI exceeds maximum length of ${MAX_URI_LENGTH} characters`);
  }

  if (!uri.startsWith("otpauth://")) {
    throw new InvalidURIError(uri);
  }

  const withoutScheme = uri.slice("otpauth://".length);
  const slashIndex = withoutScheme.indexOf("/");

  if (slashIndex === -1) {
    throw new InvalidURIError(uri);
  }

  const type = withoutScheme.slice(0, slashIndex);
  if (type !== "hotp" && type !== "totp") {
    throw new InvalidParameterError("type", type);
  }

  const remaining = withoutScheme.slice(slashIndex + 1);
  const queryIndex = remaining.indexOf("?");

  let label: string;
  let queryString: string;

  if (queryIndex === -1) {
    label = safeDecodeURIComponent(remaining, MAX_LABEL_LENGTH, "label");
    queryString = "";
  } else {
    label = safeDecodeURIComponent(remaining.slice(0, queryIndex), MAX_LABEL_LENGTH, "label");
    queryString = remaining.slice(queryIndex + 1);
  }

  const params = parseQueryString(queryString);

  if (!params.secret) {
    throw new MissingParameterError("secret");
  }

  return {
    type,
    label,
    params: params as OTPAuthParams,
  };
}

/**
 * Parse query string into parameters object
 */
function parseQueryString(queryString: string): MutableOTPAuthParams {
  const params: MutableOTPAuthParams = {};
  const seenParameters = new Set<string>();

  if (!queryString) {
    return params;
  }

  const pairs = queryString.split("&");
  for (const pair of pairs) {
    const equalIndex = pair.indexOf("=");
    const isBare = equalIndex === -1;

    let key: string;
    if (isBare) {
      try {
        key = decodeURIComponent(pair);
      } catch {
        // Preserve the existing behavior for malformed bare parameters: only
        // key/value pairs participate in strict URI decoding.
        continue;
      }
    } else {
      key = safeDecodeURIComponent(pair.slice(0, equalIndex), 64, "parameter key");
    }

    // Match URLSearchParams.get(): the first decoded occurrence determines the
    // parameter. Later values are ignored without decoding or validating them.
    if (seenParameters.has(key)) {
      continue;
    }
    seenParameters.add(key);

    const value = isBare
      ? ""
      : safeDecodeURIComponent(
          pair.slice(equalIndex + 1),
          MAX_PARAM_VALUE_LENGTH,
          `parameter '${key}'`,
        );

    switch (key) {
      case "secret":
        params.secret = value;
        break;
      case "issuer":
        params.issuer = value;
        break;
      case "algorithm":
        // `?algorithm=` carries no value. The Key Uri Format makes this
        // parameter optional with a SHA-1 default, so an empty one is treated
        // as omitted rather than as an invalid value - which is also what the
        // CLI parser does with the same URI. Any non-empty value stays strict.
        if (value !== "") {
          params.algorithm = parseAlgorithm(value);
        }
        break;
      case "digits":
        params.digits = parseDigits(value);
        break;
      case "counter":
        params.counter = parseIntegerParameter("counter", value, 0);
        break;
      case "period":
        params.period = parseIntegerParameter("period", value, 1);
        break;
    }
  }

  return params;
}

/**
 * Parse an integer parameter with strict validation
 */
function parseIntegerParameter(name: string, value: string, min: number): number {
  if (!/^-?\d+$/.test(value)) {
    throw new InvalidParameterError(name, value);
  }

  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) {
    throw new InvalidParameterError(name, value);
  }

  if (parsed < min) {
    throw new InvalidParameterError(name, value);
  }

  return parsed;
}

/**
 * Parse algorithm string
 *
 * `otpauth://` URIs come from third-party issuers, several of which emit the
 * dashed aliases (`SHA-1`, `SHA-256`). Core's `normalizeHashAlgorithm`
 * accepts those and returns the canonical lowercase name, so this only has to
 * restate the rejection in this package's error type.
 */
function parseAlgorithm(value: string): HashAlgorithm {
  try {
    return normalizeHashAlgorithm(value);
  } catch (error) {
    // Carried as cause so the AlgorithmUnsupportedError stays reachable -
    // generate() throws the core error directly, and without this bridge the
    // two directions would be programmatically unrelatable.
    throw new InvalidParameterError("algorithm", value, { cause: error });
  }
}

/**
 * Parse digits string
 */
function parseDigits(value: string): Digits {
  if (!/^\d+$/.test(value)) {
    throw new InvalidParameterError("digits", value);
  }

  const digits = Number(value);
  if (digits === 6 || digits === 7 || digits === 8) {
    return digits;
  }
  throw new InvalidParameterError("digits", value);
}
